import { useState } from "react";
import {
  ChevronDown,
  Copy,
  Film,
  Image as ImageIcon,
  Layers,
  Loader2,
  Pencil,
  RefreshCw,
  Trash2,
  Users,
} from "lucide-react";

import { fmtDateShort, fmtTime } from "../../lib/format";
import { PLATFORMS } from "../../lib/platforms";
import { PlatformIcon } from "../branding";

const slug = (s) => (s || "yourbrand").toLowerCase().replace(/[^a-z0-9_]/g, "");

// Per platform avatar treatment. Authentic platform color is allowed inside the
// preview card itself (the app chrome stays monochrome).
const AVATAR = {
  linkedin: { square: true, color: "#0A66C2" },
  facebook: { color: "#1877F2" },
  youtube: { color: "#FF0000" },
  reddit: { color: "#FF4500" },
  instagram: { ring: true },
  x: {},
  threads: {},
  tiktok: {},
};

// The link color used for hashtags and CTAs, by platform.
const LINK = {
  x: "#1d6ad1",
  linkedin: "#0A66C2",
  facebook: "#1877F2",
  instagram: "#3a6db5",
  youtube: "#3a6db5",
  threads: "rgb(var(--muted))",
  tiktok: "#3a6db5",
  reddit: "#0079D3",
};

// The media or script disclosure button shown at the foot of each card.
const MEDIA = {
  tiktok: { icon: Film, label: "Video script" },
  youtube: { icon: Film, label: "Shooting script" },
  instagram: { icon: Layers, label: "Carousel or visual idea" },
  x: { icon: ImageIcon, label: "Visual idea" },
  linkedin: { icon: ImageIcon, label: "Visual idea" },
  facebook: { icon: ImageIcon, label: "Visual idea" },
  threads: { icon: ImageIcon, label: "Visual idea" },
  reddit: { icon: ImageIcon, label: "Subreddit and link plan" },
};

function Avatar({ name, platform, size = 42 }) {
  const cfg = AVATAR[platform] || {};
  const inner = (
    <div
      className="flex items-center justify-center font-semibold text-white shrink-0"
      style={{
        width: size,
        height: size,
        borderRadius: cfg.square ? 9 : 999,
        fontSize: size * 0.42,
        background: cfg.color || "linear-gradient(135deg,#4a4a4a,#0a0a0a)",
      }}
    >
      {(name || "Y")[0].toUpperCase()}
    </div>
  );
  if (cfg.ring) {
    return (
      <div
        className="shrink-0"
        style={{
          padding: 2.5,
          borderRadius: 999,
          background: "linear-gradient(45deg,#feda75,#fa7e1e,#d62976,#962fbf,#4f5bd5)",
        }}
      >
        <div style={{ padding: 2, borderRadius: 999, background: "rgb(var(--popover))" }}>
          {inner}
        </div>
      </div>
    );
  }
  return inner;
}

function Hashtags({ tags, color }) {
  if (!tags || !tags.length) return null;
  return (
    <div className="mt-2 text-[13.5px] leading-relaxed break-words" style={{ color }}>
      {tags.map((t) => "#" + t).join(" ")}
    </div>
  );
}

function MediaDisclosure({ post }) {
  const [open, setOpen] = useState(false);
  if (!post.media_idea) return null;
  const cfg = MEDIA[post.platform] || MEDIA.x;
  const Icon = cfg.icon;
  return (
    <div className="mt-3 pt-3 border-t border-border">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 text-[12.5px] text-muted hover:text-foreground transition"
      >
        <Icon size={15} />
        <span className="font-medium">{cfg.label}</span>
        <ChevronDown
          size={14}
          className="transition-transform"
          style={{ transform: open ? "rotate(180deg)" : "none" }}
        />
      </button>
      {open ? (
        <p className="mt-2 text-[13px] text-muted whitespace-pre-wrap leading-relaxed bg-foreground/[0.03] rounded-xl p-3">
          {post.media_idea}
        </p>
      ) : null}
    </div>
  );
}

function PlanBlock({ post }) {
  if (!(post.title || post.format || post.hook || post.caption_direction)) return null;
  return (
    <div className="mb-2 rounded-2xl border border-border bg-surface p-3.5 space-y-2.5">
      {post.title || post.format ? (
        <div className="flex items-start justify-between gap-3">
          {post.title ? (
            <div className="font-semibold text-[14px] text-foreground leading-snug">{post.title}</div>
          ) : (
            <span />
          )}
          {post.format ? (
            <span className="shrink-0 text-[11px] font-medium text-muted bg-foreground/[0.06] rounded-full px-2.5 py-1 whitespace-nowrap">
              {post.format}
            </span>
          ) : null}
        </div>
      ) : null}
      {post.hook ? (
        <div>
          <div className="text-[10.5px] font-semibold uppercase tracking-wide text-faint mb-0.5">Hook</div>
          <div className="text-[13.5px] text-foreground leading-relaxed">{post.hook}</div>
        </div>
      ) : null}
      {post.caption_direction ? (
        <div>
          <div className="text-[10.5px] font-semibold uppercase tracking-wide text-faint mb-0.5">Caption direction</div>
          <div className="text-[13px] text-muted leading-relaxed">{post.caption_direction}</div>
        </div>
      ) : null}
    </div>
  );
}

// One consistent card layout for every platform: same width, caption first,
// with small native touches (avatar accent, link color) and a script or visual
// disclosure at the foot.
function CardBody({ post }) {
  const name = post.org || "yourbrand";
  const handle = post.handle || slug(name);
  const handlePrefix = post.platform === "reddit" ? "u/" : "@";
  const link = LINK[post.platform] || "#1d6ad1";

  return (
    <div className="bg-popover border border-border rounded-2xl p-4">
      <div className="flex gap-3 items-center">
        <Avatar name={name} platform={post.platform} size={42} />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-foreground text-[14.5px] leading-tight truncate">
            {name}
          </div>
          <div className="text-[12.5px] text-faint leading-tight truncate">
            {handlePrefix}{handle} · {fmtTime(post.time)}
          </div>
        </div>
      </div>

      <p className="mt-3 text-[14.5px] text-foreground whitespace-pre-wrap leading-relaxed break-words">
        {post.body}
      </p>

      <Hashtags tags={post.hashtags} color={link} />

      {post.cta ? (
        <p className="mt-2 text-[14px] font-medium break-words" style={{ color: link }}>
          {post.cta}
        </p>
      ) : null}

      <MediaDisclosure post={post} />
    </div>
  );
}

export default function PostCard({ post, onEdit, onDelete, onRegenerate, onCopy, onStoryboard, onUgc, regenerating }) {
  const meta = PLATFORMS[post.platform] || { name: post.platform };

  const ToolBtn = ({ title, onClick, children }) => (
    <button
      onClick={onClick}
      title={title}
      className="w-7 h-7 rounded-lg flex items-center justify-center text-muted hover:bg-foreground/5 hover:text-foreground transition active:scale-[0.97]"
    >
      {children}
    </button>
  );

  return (
    <div className="animate-fade-in">
      <div className="flex items-center gap-1.5 mb-1.5 px-0.5 text-[12px]">
        <PlatformIcon id={post.platform} size={13} className="text-foreground shrink-0" />
        <span className="font-semibold text-foreground">{meta.name}</span>
        <span className="text-faint">·</span>
        <span className="text-faint truncate">
          {fmtDateShort(post.date)}, {fmtTime(post.time)}
        </span>
        <div className="ml-auto flex items-center -mr-1">
          {onStoryboard ? (
            <ToolBtn title="Storyboard and script" onClick={onStoryboard}>
              <Film size={14} />
            </ToolBtn>
          ) : null}
          {onUgc ? (
            <ToolBtn title="UGC brief and script" onClick={onUgc}>
              <Users size={14} />
            </ToolBtn>
          ) : null}
          {onCopy ? (
            <ToolBtn title="Copy" onClick={onCopy}>
              <Copy size={14} />
            </ToolBtn>
          ) : null}
          {onRegenerate ? (
            <ToolBtn title="Regenerate this post" onClick={onRegenerate}>
              {regenerating ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            </ToolBtn>
          ) : null}
          {onEdit ? (
            <ToolBtn title="Edit" onClick={onEdit}>
              <Pencil size={14} />
            </ToolBtn>
          ) : null}
          {onDelete ? (
            <ToolBtn title="Delete" onClick={onDelete}>
              <Trash2 size={14} />
            </ToolBtn>
          ) : null}
        </div>
      </div>
      <PlanBlock post={post} />
      <CardBody post={post} />
    </div>
  );
}